import { useApolloClient, ApolloClient, QueryOptions } from '@apollo/client'

import {
  AgreeToServiceTermsDocument,
  AgreeToServiceTermsMutationResult,
  AgreeToServiceTermsMutationVariables,
  CreateAdminInvitationDocument,
  CreateAdminInvitationMutationResult,
  CreateAdminInvitationMutationVariables,
  CreateAdminPlanDocument,
  CreateAdminPlanMutationResult,
  CreateAdminPlanMutationVariables,
  CreateInvitationDocument,
  CreateInvitationMutationResult,
  CreateInvitationMutationVariables,
  CreateMillsheetsByCsvDocument,
  CreateMillsheetsByCsvMutationResult,
  CreateMillsheetsByCsvMutationVariables,
  CreateS3ObjectDocument,
  CreateS3ObjectMutationResult,
  CreateS3ObjectMutationVariables,
  CreateS3ObjectThumbnailsDocument,
  CreateS3ObjectThumbnailsMutationResult,
  CreateS3ObjectThumbnailsMutationVariables,
  CreateTenantDocument,
  CreateTenantMutationResult,
  CreateTenantMutationVariables,
  DeleteAdminPlanDocument,
  DeleteAdminPlanMutationResult,
  DeleteAdminPlanMutationVariables,
  DeleteAdminUserDocument,
  DeleteAdminUserMutationResult,
  DeleteAdminUserMutationVariables,
  DeleteMillsheetDocument,
  DeleteMillsheetMutationResult,
  DeleteMillsheetMutationVariables,
  DeleteS3ObjectDocument,
  DeleteS3ObjectMutationResult,
  DeleteS3ObjectMutationVariables,
  DeleteTenantDocument,
  DeleteTenantMutationResult,
  DeleteTenantMutationVariables,
  DeleteUserDocument,
  DeleteUserMutationResult,
  DeleteUserMutationVariables,
  FetchAdminPlansDocument,
  FetchAdminPlansQuery,
  FetchAdminPlansQueryResult,
  FetchAdminPlansQueryVariables,
  FetchAdminS3ObjectDocument,
  FetchAdminS3ObjectQuery,
  FetchAdminS3ObjectQueryResult,
  FetchAdminS3ObjectQueryVariables,
  FetchAdminTenantsDocument,
  FetchAdminTenantsQuery,
  FetchAdminTenantsQueryResult,
  FetchAdminTenantsQueryVariables,
  FetchDashboardDocument,
  FetchDashboardQuery,
  FetchDashboardQueryResult,
  FetchDashboardQueryVariables,
  FetchMeDocument,
  FetchMeQuery,
  FetchMeQueryResult,
  FetchMeQueryVariables,
  FetchMillsheetItemsDocument,
  FetchMillsheetItemsQuery,
  FetchMillsheetItemsQueryResult,
  FetchMillsheetItemsQueryVariables,
  FetchMillsheetsDocument,
  FetchMillsheetsQuery,
  FetchMillsheetsQueryResult,
  FetchMillsheetsQueryVariables,
  FetchMyIdentityDocument,
  FetchMyIdentityQuery,
  FetchMyIdentityQueryResult,
  FetchMyIdentityQueryVariables,
  FetchMyTenantDetailDocument,
  FetchMyTenantDetailQuery,
  FetchMyTenantDetailQueryResult,
  FetchMyTenantDetailQueryVariables,
  FetchS3ObjectsDocument,
  FetchS3ObjectsQuery,
  FetchS3ObjectsQueryResult,
  FetchS3ObjectsQueryVariables,
  FetchTenantDocument,
  FetchTenantQuery,
  FetchTenantQueryResult,
  FetchTenantQueryVariables,
  FetchTenantsDocument,
  FetchTenantsQuery,
  FetchTenantsQueryResult,
  FetchTenantsQueryVariables,
  FetchUsersDocument,
  FetchUsersQuery,
  FetchUsersQueryResult,
  FetchUsersQueryVariables,
  UpdateAdminPlanDocument,
  UpdateAdminPlanMutationResult,
  UpdateAdminPlanMutationVariables,
  UpdateMillsheetDocument,
  UpdateMillsheetMutationResult,
  UpdateMillsheetMutationVariables,
  UpdateMillsheetsDocument,
  UpdateMillsheetsMutationResult,
  UpdateMillsheetsMutationVariables,
  UpdateS3ObjectDocument,
  UpdateS3ObjectMutationResult,
  UpdateS3ObjectMutationVariables,
  UpdateTenantDocument,
  UpdateTenantMutationResult,
  UpdateTenantMutationVariables,
  UpdateTenantSettingDocument,
  UpdateTenantSettingMutationResult,
  UpdateTenantSettingMutationVariables,
  UpdateUserDocument,
  UpdateUserMutationResult,
  UpdateUserMutationVariables,
  UpsertAdminTenantSettingDocument,
  UpsertAdminTenantSettingMutationResult,
  UpsertAdminTenantSettingMutationVariables,
  useAgreeToServiceTermsMutation,
  useCreateAdminInvitationMutation,
  useCreateAdminPlanMutation,
  useCreateInvitationMutation,
  useCreateMillsheetsByCsvMutation,
  useCreateS3ObjectMutation,
  useCreateS3ObjectThumbnailsMutation,
  useCreateTenantMutation,
  useDeleteAdminPlanMutation,
  useDeleteAdminUserMutation,
  useDeleteMillsheetMutation,
  useDeleteS3ObjectMutation,
  useDeleteTenantMutation,
  useDeleteUserMutation,
  useFetchAdminPlansQuery,
  useFetchAdminPlansSuspenseQuery,
  useFetchAdminS3ObjectQuery,
  useFetchAdminS3ObjectSuspenseQuery,
  useFetchAdminTenantsQuery,
  useFetchAdminTenantsSuspenseQuery,
  useFetchDashboardQuery,
  useFetchDashboardSuspenseQuery,
  useFetchMeQuery,
  useFetchMeSuspenseQuery,
  useFetchMillsheetItemsQuery,
  useFetchMillsheetItemsSuspenseQuery,
  useFetchMillsheetsQuery,
  useFetchMillsheetsSuspenseQuery,
  useFetchMyIdentityQuery,
  useFetchMyIdentitySuspenseQuery,
  useFetchMyTenantDetailQuery,
  useFetchMyTenantDetailSuspenseQuery,
  useFetchS3ObjectsQuery,
  useFetchS3ObjectsSuspenseQuery,
  useFetchTenantQuery,
  useFetchTenantsQuery,
  useFetchTenantsSuspenseQuery,
  useFetchTenantSuspenseQuery,
  useFetchUsersQuery,
  useFetchUsersSuspenseQuery,
  useUpdateAdminPlanMutation,
  useUpdateMillsheetMutation,
  useUpdateMillsheetsMutation,
  useUpdateS3ObjectMutation,
  useUpdateTenantMutation,
  useUpdateTenantSettingMutation,
  useUpdateUserMutation,
  useUpsertAdminTenantSettingMutation,
} from './apollo'
import { useEffect } from 'react'
import { MutationBaseOptions } from '@apollo/client/core/watchQueryOptions'

//
// fetchAdminS3Object
//
const fetchAdminS3Object = async (
  client: ApolloClient<unknown>,
  options: Omit<QueryOptions<FetchAdminS3ObjectQueryVariables>, 'query'>,
) => {
  const result = await client.query<FetchAdminS3ObjectQueryResult['data']>({
    ...options,
    query: FetchAdminS3ObjectDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

//
// fetchAdminTenants
//
const fetchAdminTenants = async (
  client: ApolloClient<unknown>,
  options: Omit<QueryOptions<FetchAdminTenantsQueryVariables>, 'query'>,
) => {
  const result = await client.query<FetchAdminTenantsQueryResult['data']>({
    ...options,
    query: FetchAdminTenantsDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

//
// fetchDashboard
//
const fetchDashboard = async (
  client: ApolloClient<unknown>,
  options: Omit<QueryOptions<FetchDashboardQueryVariables>, 'query'>,
) => {
  const result = await client.query<FetchDashboardQueryResult['data']>({
    ...options,
    query: FetchDashboardDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

//
// fetchMe
//
const fetchMe = async (client: ApolloClient<unknown>, options: Omit<QueryOptions<FetchMeQueryVariables>, 'query'>) => {
  const result = await client.query<FetchMeQueryResult['data']>({
    ...options,
    query: FetchMeDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

//
// fetchMillsheetItems
//
const fetchMillsheetItems = async (
  client: ApolloClient<unknown>,
  options: Omit<QueryOptions<FetchMillsheetItemsQueryVariables>, 'query'>,
) => {
  const result = await client.query<FetchMillsheetItemsQueryResult['data']>({
    ...options,
    query: FetchMillsheetItemsDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

//
// fetchMillsheets
//
const fetchMillsheets = async (
  client: ApolloClient<unknown>,
  options: Omit<QueryOptions<FetchMillsheetsQueryVariables>, 'query'>,
) => {
  const result = await client.query<FetchMillsheetsQueryResult['data']>({
    ...options,
    query: FetchMillsheetsDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

//
// fetchMyIdentity
//
const fetchMyIdentity = async (
  client: ApolloClient<unknown>,
  options: Omit<QueryOptions<FetchMyIdentityQueryVariables>, 'query'>,
) => {
  const result = await client.query<FetchMyIdentityQueryResult['data']>({
    ...options,
    query: FetchMyIdentityDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

//
// fetchMyTenantDetail
//
const fetchMyTenantDetail = async (
  client: ApolloClient<unknown>,
  options: Omit<QueryOptions<FetchMyTenantDetailQueryVariables>, 'query'>,
) => {
  const result = await client.query<FetchMyTenantDetailQueryResult['data']>({
    ...options,
    query: FetchMyTenantDetailDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

//
// fetchAdminPlans
//
const fetchAdminPlans = async (
  client: ApolloClient<unknown>,
  options: Omit<QueryOptions<FetchAdminPlansQueryVariables>, 'query'>,
) => {
  const result = await client.query<FetchAdminPlansQueryResult['data']>({
    ...options,
    query: FetchAdminPlansDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

//
// fetchS3Objects
//
const fetchS3Objects = async (
  client: ApolloClient<unknown>,
  options: Omit<QueryOptions<FetchS3ObjectsQueryVariables>, 'query'>,
) => {
  const result = await client.query<FetchS3ObjectsQueryResult['data']>({
    ...options,
    query: FetchS3ObjectsDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

//
// fetchTenant
//
const fetchTenant = async (
  client: ApolloClient<unknown>,
  options: Omit<QueryOptions<FetchTenantQueryVariables>, 'query'>,
) => {
  const result = await client.query<FetchTenantQueryResult['data']>({
    ...options,
    query: FetchTenantDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

//
// fetchTenants
//
const fetchTenants = async (
  client: ApolloClient<unknown>,
  options: Omit<QueryOptions<FetchTenantsQueryVariables>, 'query'>,
) => {
  const result = await client.query<FetchTenantsQueryResult['data']>({
    ...options,
    query: FetchTenantsDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

//
// fetchUsers
//
const fetchUsers = async (
  client: ApolloClient<unknown>,
  options: Omit<QueryOptions<FetchUsersQueryVariables>, 'query'>,
) => {
  const result = await client.query<FetchUsersQueryResult['data']>({
    ...options,
    query: FetchUsersDocument,
  })
  if (result.error !== undefined) {
    throw new Error(result.error.message)
  }
  if (result.data === undefined) {
    throw new Error('データの取得に失敗しました')
  }
  return result.data
}

const createInvitation = async (
  client: ApolloClient<unknown>,
  variables: CreateInvitationMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<CreateInvitationMutationResult['data']>({
    ...options,
    mutation: CreateInvitationDocument,
    variables,
  })
  return result
}

const agreeToServiceTerms = async (
  client: ApolloClient<unknown>,
  variables: AgreeToServiceTermsMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<AgreeToServiceTermsMutationResult['data']>({
    ...options,
    mutation: AgreeToServiceTermsDocument,
    variables,
  })
  return result
}

const createAdminInvitation = async (
  client: ApolloClient<unknown>,
  variables: CreateAdminInvitationMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<CreateAdminInvitationMutationResult['data']>({
    ...options,
    mutation: CreateAdminInvitationDocument,
    variables,
  })
  return result
}

const createAdminPlan = async (
  client: ApolloClient<unknown>,
  variables: CreateAdminPlanMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<CreateAdminPlanMutationResult['data']>({
    ...options,
    mutation: CreateAdminPlanDocument,
    variables,
  })
  return result
}

const createMillsheetsByCsv = async (
  client: ApolloClient<unknown>,
  variables: CreateMillsheetsByCsvMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<CreateMillsheetsByCsvMutationResult['data']>({
    ...options,
    mutation: CreateMillsheetsByCsvDocument,
    variables,
  })
  return result
}

const createS3Object = async (
  client: ApolloClient<unknown>,
  variables: CreateS3ObjectMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<CreateS3ObjectMutationResult['data']>({
    ...options,
    mutation: CreateS3ObjectDocument,
    variables,
  })
  return result
}

const createS3ObjectThumbnails = async (
  client: ApolloClient<unknown>,
  variables: CreateS3ObjectThumbnailsMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<CreateS3ObjectThumbnailsMutationResult['data']>({
    ...options,
    mutation: CreateS3ObjectThumbnailsDocument,
    variables,
  })
  return result
}

const createTenant = async (
  client: ApolloClient<unknown>,
  variables: CreateTenantMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<CreateTenantMutationResult['data']>({
    ...options,
    mutation: CreateTenantDocument,
    variables,
  })
  return result
}

const deleteAdminPlan = async (
  client: ApolloClient<unknown>,
  variables: DeleteAdminPlanMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<DeleteAdminPlanMutationResult['data']>({
    ...options,
    mutation: DeleteAdminPlanDocument,
    variables,
  })
  return result
}

const deleteAdminUser = async (
  client: ApolloClient<unknown>,
  variables: DeleteAdminUserMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<DeleteAdminUserMutationResult['data']>({
    ...options,
    mutation: DeleteAdminUserDocument,
    variables,
  })
  return result
}

const deleteMillsheet = async (
  client: ApolloClient<unknown>,
  variables: DeleteMillsheetMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<DeleteMillsheetMutationResult['data']>({
    ...options,
    mutation: DeleteMillsheetDocument,
    variables,
  })
  return result
}

const deleteS3Object = async (
  client: ApolloClient<unknown>,
  variables: DeleteS3ObjectMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<DeleteS3ObjectMutationResult['data']>({
    ...options,
    mutation: DeleteS3ObjectDocument,
    variables,
  })
  return result
}

const deleteTenant = async (
  client: ApolloClient<unknown>,
  variables: DeleteTenantMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<DeleteTenantMutationResult['data']>({
    ...options,
    mutation: DeleteTenantDocument,
    variables,
  })
  return result
}

const deleteUser = async (
  client: ApolloClient<unknown>,
  variables: DeleteUserMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<DeleteUserMutationResult['data']>({
    ...options,
    mutation: DeleteUserDocument,
    variables,
  })
  return result
}

const updateAdminPlan = async (
  client: ApolloClient<unknown>,
  variables: UpdateAdminPlanMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<UpdateAdminPlanMutationResult['data']>({
    ...options,
    mutation: UpdateAdminPlanDocument,
    variables,
  })
  return result
}

const updateMillsheet = async (
  client: ApolloClient<unknown>,
  variables: UpdateMillsheetMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<UpdateMillsheetMutationResult['data']>({
    ...options,
    mutation: UpdateMillsheetDocument,
    variables,
  })
  return result
}

const updateMillsheets = async (
  client: ApolloClient<unknown>,
  variables: UpdateMillsheetsMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<UpdateMillsheetsMutationResult['data']>({
    ...options,
    mutation: UpdateMillsheetsDocument,
    variables,
  })
  return result
}

const updateS3Object = async (
  client: ApolloClient<unknown>,
  variables: UpdateS3ObjectMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<UpdateS3ObjectMutationResult['data']>({
    ...options,
    mutation: UpdateS3ObjectDocument,
    variables,
  })
  return result
}

const updateTenant = async (
  client: ApolloClient<unknown>,
  variables: UpdateTenantMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<UpdateTenantMutationResult['data']>({
    ...options,
    mutation: UpdateTenantDocument,
    variables,
  })
  return result
}

const updateTenantSetting = async (
  client: ApolloClient<unknown>,
  variables: UpdateTenantSettingMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<UpdateTenantSettingMutationResult['data']>({
    ...options,
    mutation: UpdateTenantSettingDocument,
    variables,
  })
  return result
}

const updateUser = async (
  client: ApolloClient<unknown>,
  variables: UpdateUserMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<UpdateUserMutationResult['data']>({
    ...options,
    mutation: UpdateUserDocument,
    variables,
  })
  return result
}

const upsertAdminTenantSetting = async (
  client: ApolloClient<unknown>,
  variables: UpsertAdminTenantSettingMutationVariables,
  options?: MutationBaseOptions,
) => {
  const result = await client.mutate<UpsertAdminTenantSettingMutationResult['data']>({
    ...options,
    mutation: UpsertAdminTenantSettingDocument,
    variables,
  })
  return result
}

const useClient = () => {
  const client = useApolloClient()
  return {
    fetchAdminS3Object: async (
      variables: FetchAdminS3ObjectQueryVariables,
      options?: Omit<QueryOptions<FetchAdminS3ObjectQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchAdminS3Object(client, { ...options, variables })
    },
    fetchAdminTenants: async (
      variables: FetchAdminTenantsQueryVariables,
      options?: Omit<QueryOptions<FetchAdminTenantsQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchAdminTenants(client, { ...options, variables })
    },
    fetchDashboard: async (
      variables: FetchDashboardQueryVariables,
      options?: Omit<QueryOptions<FetchDashboardQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchDashboard(client, { ...options, variables })
    },
    fetchMe: async (
      variables: FetchMeQueryVariables,
      options?: Omit<QueryOptions<FetchMeQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchMe(client, { ...options, variables })
    },
    fetchMillsheetItems: async (
      variables: FetchMillsheetItemsQueryVariables,
      options?: Omit<QueryOptions<FetchMillsheetItemsQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchMillsheetItems(client, { ...options, variables })
    },
    fetchMillsheets: async (
      variables: FetchMillsheetsQueryVariables,
      options?: Omit<QueryOptions<FetchMillsheetsQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchMillsheets(client, { ...options, variables })
    },
    fetchMyIdentity: async (
      variables: FetchMyIdentityQueryVariables,
      options?: Omit<QueryOptions<FetchMyIdentityQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchMyIdentity(client, { ...options, variables })
    },
    fetchMyTenantDetail: async (
      variables: FetchMyTenantDetailQueryVariables,
      options?: Omit<QueryOptions<FetchMyTenantDetailQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchMyTenantDetail(client, { ...options, variables })
    },
    fetchAdminPlans: async (
      variables: FetchAdminPlansQueryVariables,
      options?: Omit<QueryOptions<FetchAdminPlansQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchAdminPlans(client, { ...options, variables })
    },
    fetchS3Objects: async (
      variables: FetchS3ObjectsQueryVariables,
      options?: Omit<QueryOptions<FetchS3ObjectsQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchS3Objects(client, { ...options, variables })
    },
    fetchTenant: async (
      variables: FetchTenantQueryVariables,
      options?: Omit<QueryOptions<FetchTenantQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchTenant(client, { ...options, variables })
    },
    fetchTenants: async (
      variables: FetchTenantsQueryVariables,
      options?: Omit<QueryOptions<FetchTenantsQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchTenants(client, { ...options, variables })
    },
    fetchUsers: async (
      variables: FetchUsersQueryVariables,
      options?: Omit<QueryOptions<FetchUsersQueryVariables>, 'query' | 'variables'>,
    ) => {
      return await fetchUsers(client, { ...options, variables })
    },
    createInvitation: async (variables: CreateInvitationMutationVariables, options?: MutationBaseOptions) => {
      return await createInvitation(client, variables, options)
    },
    agreeToServiceTerms: async (variables: AgreeToServiceTermsMutationVariables, options?: MutationBaseOptions) => {
      return await agreeToServiceTerms(client, variables, options)
    },
    createAdminInvitation: async (variables: CreateAdminInvitationMutationVariables, options?: MutationBaseOptions) => {
      return await createAdminInvitation(client, variables, options)
    },
    createAdminPlan: async (variables: CreateAdminPlanMutationVariables, options?: MutationBaseOptions) => {
      return await createAdminPlan(client, variables, options)
    },
    createMillsheetsByCsv: async (variables: CreateMillsheetsByCsvMutationVariables, options?: MutationBaseOptions) => {
      return await createMillsheetsByCsv(client, variables, options)
    },
    createS3Object: async (variables: CreateS3ObjectMutationVariables, options?: MutationBaseOptions) => {
      return await createS3Object(client, variables, options)
    },
    createS3ObjectThumbnails: async (
      variables: CreateS3ObjectThumbnailsMutationVariables,
      options?: MutationBaseOptions,
    ) => {
      return await createS3ObjectThumbnails(client, variables, options)
    },
    createTenant: async (variables: CreateTenantMutationVariables, options?: MutationBaseOptions) => {
      return await createTenant(client, variables, options)
    },
    deleteAdminPlan: async (variables: DeleteAdminPlanMutationVariables, options?: MutationBaseOptions) => {
      return await deleteAdminPlan(client, variables, options)
    },
    deleteAdminUser: async (variables: DeleteAdminUserMutationVariables, options?: MutationBaseOptions) => {
      return await deleteAdminUser(client, variables, options)
    },
    deleteMillsheet: async (variables: DeleteMillsheetMutationVariables, options?: MutationBaseOptions) => {
      return await deleteMillsheet(client, variables, options)
    },
    deleteS3Object: async (variables: DeleteS3ObjectMutationVariables, options?: MutationBaseOptions) => {
      return await deleteS3Object(client, variables, options)
    },
    deleteTenant: async (variables: DeleteTenantMutationVariables, options?: MutationBaseOptions) => {
      return await deleteTenant(client, variables, options)
    },
    deleteUser: async (variables: DeleteUserMutationVariables, options?: MutationBaseOptions) => {
      return await deleteUser(client, variables, options)
    },
    updateAdminPlan: async (variables: UpdateAdminPlanMutationVariables, options?: MutationBaseOptions) => {
      return await updateAdminPlan(client, variables, options)
    },
    updateMillsheet: async (variables: UpdateMillsheetMutationVariables, options?: MutationBaseOptions) => {
      return await updateMillsheet(client, variables, options)
    },
    updateMillsheets: async (variables: UpdateMillsheetsMutationVariables, options?: MutationBaseOptions) => {
      return await updateMillsheets(client, variables, options)
    },
    updateS3Object: async (variables: UpdateS3ObjectMutationVariables, options?: MutationBaseOptions) => {
      return await updateS3Object(client, variables, options)
    },
    updateTenant: async (variables: UpdateTenantMutationVariables, options?: MutationBaseOptions) => {
      return await updateTenant(client, variables, options)
    },
    updateTenantSetting: async (variables: UpdateTenantSettingMutationVariables, options?: MutationBaseOptions) => {
      return await updateTenantSetting(client, variables, options)
    },
    updateUser: async (variables: UpdateUserMutationVariables, options?: MutationBaseOptions) => {
      return await updateUser(client, variables, options)
    },
    upsertAdminTenantSetting: async (
      variables: UpsertAdminTenantSettingMutationVariables,
      options?: MutationBaseOptions,
    ) => {
      return await upsertAdminTenantSetting(client, variables, options)
    },
  }
}

export const api = {
  useClient,
  useFetchAdminS3ObjectSuspenseQuery,
  useFetchAdminTenantsSuspenseQuery,
  useFetchDashboardSuspenseQuery,
  useFetchMeSuspenseQuery,
  useFetchMillsheetItemsSuspenseQuery,
  useFetchMillsheetsSuspenseQuery,
  useFetchMyIdentitySuspenseQuery,
  useFetchMyTenantDetailSuspenseQuery,
  useFetchAdminPlansSuspenseQuery,
  useFetchS3ObjectsSuspenseQuery,
  useFetchTenantSuspenseQuery,
  useFetchTenantsSuspenseQuery,
  useFetchUsersSuspenseQuery,
  useFetchAdminS3ObjectQuery,
  useFetchAdminTenantsQuery,
  useFetchDashboardQuery,
  useFetchMeQuery,
  useFetchMillsheetItemsQuery,
  useFetchMillsheetsQuery,
  useFetchMyIdentityQuery,
  useFetchMyTenantDetailQuery,
  useFetchAdminPlansQuery,
  useFetchS3ObjectsQuery,
  useFetchTenantQuery,
  useFetchTenantsQuery,
  useFetchUsersQuery,
  useCreateInvitationMutation,
  useAgreeToServiceTermsMutation,
  useCreateAdminInvitationMutation,
  useCreateAdminPlanMutation,
  useCreateMillsheetsByCsvMutation,
  useCreateS3ObjectMutation,
  useCreateS3ObjectThumbnailsMutation,
  useCreateTenantMutation,
  useDeleteAdminPlanMutation,
  useDeleteAdminUserMutation,
  useDeleteMillsheetMutation,
  useDeleteS3ObjectMutation,
  useDeleteTenantMutation,
  useDeleteUserMutation,
  useUpdateAdminPlanMutation,
  useUpdateMillsheetMutation,
  useUpdateMillsheetsMutation,
  useUpdateS3ObjectMutation,
  useUpdateTenantMutation,
  useUpdateTenantSettingMutation,
  useUpdateUserMutation,
  useUpsertAdminTenantSettingMutation,
}

export type ApiClient = ReturnType<typeof useClient>
